import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import AuthContext from './authContext';
import useUser from '../hooks/useUser';

const STORAGE_KEY = 'fz-management-colorMode';

const VALID_MODES = new Set(['light', 'dark', 'system']);

function readStoredMode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_MODES.has(raw)) return raw;
  } catch (_e) {
    /* ignore */
  }
  return null;
}

const ColorModeContext = createContext({
  colorModePreference: 'dark',
  colorModePreferenceForDisplay: 'dark',
  resolvedPaletteMode: 'dark',
  setColorModePreference: async () => {},
  introductionThemePreview: null,
  setIntroductionThemePreview: () => {},
});

export function ColorModeProvider({ children }) {
  const authCtx = useContext(AuthContext);
  const { updateUserPreferences } = useUser();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });

  const serverMode = authCtx?.preferences?.colorMode;
  const isLoggedIn = Boolean(authCtx?.token);
  const userPreferencesHydrated = Boolean(authCtx?.userPreferencesHydrated);

  const [localPreference, setLocalPreference] = useState(() => {
    const stored = readStoredMode();
    if (stored) return stored;
    return 'dark';
  });

  const [introductionThemePreview, setIntroductionThemePreview] = useState(null);

  const colorModePreference = useMemo(() => {
    if (
      isLoggedIn &&
      userPreferencesHydrated &&
      serverMode &&
      VALID_MODES.has(serverMode)
    ) {
      return serverMode;
    }
    return localPreference;
  }, [isLoggedIn, userPreferencesHydrated, serverMode, localPreference]);

  const colorModePreferenceForDisplay = useMemo(() => {
    if (introductionThemePreview !== null && VALID_MODES.has(introductionThemePreview)) {
      return introductionThemePreview;
    }
    return colorModePreference;
  }, [introductionThemePreview, colorModePreference]);

  useEffect(() => {
    if (!isLoggedIn || !userPreferencesHydrated) return;
    if (serverMode && VALID_MODES.has(serverMode)) {
      setLocalPreference(serverMode);
      try {
        localStorage.setItem(STORAGE_KEY, serverMode);
      } catch (_e) {
        /* ignore */
      }
    }
  }, [isLoggedIn, userPreferencesHydrated, serverMode]);

  const resolvedPaletteMode = useMemo(() => {
    const pref =
      introductionThemePreview !== null && VALID_MODES.has(introductionThemePreview)
        ? introductionThemePreview
        : colorModePreference;

    if (pref === 'system') {
      return prefersDark ? 'dark' : 'light';
    }
    return pref === 'light' ? 'light' : 'dark';
  }, [introductionThemePreview, colorModePreference, prefersDark]);

  const setColorModePreference = useCallback(
    async next => {
      const mode = String(next || '').trim();
      if (!VALID_MODES.has(mode)) return;

      setLocalPreference(mode);
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch (_e) {
        /* ignore */
      }

      if (isLoggedIn && authCtx?.setPreferences) {
        const updated = await updateUserPreferences({ colorMode: mode });
        if (updated?.preferences) {
          authCtx.setPreferences(updated.preferences);
        }
      }
    },
    [isLoggedIn, authCtx, updateUserPreferences]
  );

  const value = useMemo(
    () => ({
      colorModePreference,
      colorModePreferenceForDisplay,
      resolvedPaletteMode,
      setColorModePreference,
      introductionThemePreview,
      setIntroductionThemePreview,
    }),
    [
      colorModePreference,
      colorModePreferenceForDisplay,
      resolvedPaletteMode,
      setColorModePreference,
      introductionThemePreview,
    ]
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode() {
  return useContext(ColorModeContext);
}
